import React from 'react';
import { Clock, BookOpen, Plus, Pencil } from 'lucide-react';
import type { CourseModule, CourseLesson } from '../../../types/course';

interface ModuleListProps {
  modules: CourseModule[];
  onAddLesson: (moduleId: string) => void;
  onEditModule: (module: CourseModule) => void;
  onEditLesson?: (moduleId: string, lesson: CourseLesson) => void;
}

export function ModuleList({ 
  modules, 
  onAddLesson, 
  onEditModule,
  onEditLesson 
}: ModuleListProps) {
  if (!modules || modules.length === 0) {
    return (
      <div className="text-center py-8 bg-gray-800 rounded-lg">
        <p className="text-gray-300">No modules available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {modules.map((module) => (
        <div
          key={module.id}
          className="bg-gray-900 rounded-lg p-4 border border-gray-800"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-medium text-white">{module.title}</h3>
              {module.description && (
                <p className="text-sm text-gray-400 mt-1">{module.description}</p>
              )}
              <div className="flex items-center text-sm text-gray-400 mt-1">
                <Clock className="h-4 w-4 mr-1" />
                {module.duration}
              </div>
            </div>
            <button
              onClick={() => onEditModule(module)}
              className="text-emerald-500 hover:text-emerald-400 transition-colors"
            >
              <Pencil className="h-4 w-4" />
            </button>
          </div>

          <div className="space-y-2">
            {module.lessons?.map((lesson) => (
              <div
                key={lesson.id}
                className="bg-gray-800 p-3 rounded-lg flex items-center justify-between"
              >
                <div>
                  <h4 className="text-white font-medium">{lesson.title}</h4>
                  <div className="flex items-center text-sm text-gray-400 mt-1">
                    <BookOpen className="h-4 w-4 mr-1" />
                    {lesson.type} • {lesson.duration}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {onEditLesson && (
                    <button
                      onClick={() => onEditLesson(module.id, lesson)}
                      className="text-emerald-500 hover:text-emerald-400"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                  )}
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    lesson.status === 'published' 
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
                      : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300'
                  }`}>
                    {lesson.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
          <button
            onClick={() => onAddLesson(module.id)}
            className="mt-4 flex items-center text-sm text-emerald-500 hover:text-emerald-400 transition-colors"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Lesson
          </button>
        </div>
      ))}
    </div>
  );
}